import { FormikProps, getIn } from "formik";
import { FormField } from "./FormField";
import { Grid, Theme, Typography, useTheme } from "@mui/material";

export function GetLabelColor(
  formik: FormikProps<Record<string, unknown>>,
  field: FormField,
  theme: Theme,
) {
  // getIn on BOTH sides. `errors` was read flat while `touched` was read by path, so inside a
  // FieldArray ("licenses[0].number") the error colour never applied — a field could be red-outlined
  // by TextField's own `error` prop while its fixed label stayed the normal colour. Same flat-vs-
  // nested split FE-4 fixed across the renderers; this site was missed because it reads `errors`
  // only to test truthiness.
  if (
    getIn(formik.touched, field.id) &&
    Boolean(getIn(formik.errors, field.id))
  )
    return theme.palette.error.main;

  return theme.palette.form?.label;
}

/**
 * The caption shown above a field that opted out of MUI's floating label (`fixedLabel`).
 *
 * NEW-33. This was a bare `<Typography>` — i.e. a `<p>` sitting next to the input — while the same
 * fields pass `label={!field.fixedLabel && field.text}`, so MUI renders NO label for them. The
 * result was an input with no accessible name at all: nothing for a screen reader to announce, and
 * nothing for `getByLabelText` to find, which is why the tests covering these fields had to reach
 * for `#id` selectors. That workaround in a test is the tell, not a style preference.
 *
 * `htmlFor` points at `field.id`, which is what these renderers pass to the underlying input's `id`
 * — so the association is real rather than positional. `display: block` is explicit because a
 * `<label>` is inline by default and the `<p>` it replaces was not; without it every one of these
 * fields would reflow.
 */
export function FixedLabel({
  formik,
  field,
}: {
  formik: FormikProps<Record<string, unknown>>;
  field: FormField;
}) {
  const theme = useTheme();

  return (
    <Typography
      component="label"
      htmlFor={field.id}
      sx={{ color: GetLabelColor(formik, field, theme), display: "block" }}
    >
      {field.text}
    </Typography>
  );
}

export function maybePadRight(field: FormField) {
  if (field.padRight) {
    return <Grid size={{ xs: 12, sm: 6 }} />;
  }
  return null;
}
